import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { Visibility as ViewIcon, Close as CloseIcon, Star as StarIcon, Sms as SmsIcon } from '@mui/icons-material';
import ApiService from '../services/api';
import { AccountContact } from '../types/account';

type CommEntry = {
  id: string;
  to_email: string;
  subject: string;
  body_html: string | null;
  email_type: string;
  reference_label: string | null;
  sent_at: string;
  sent_by_user_id: string | null;
};

const EMAIL_TYPE_LABELS: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error' }> = {
  order_confirmation: { label: 'Order confirmation', color: 'primary' },
  order_approval:     { label: 'Approval request',   color: 'warning' },
  order_signed_pdf:   { label: 'Approved order',     color: 'success' },
  service_active:     { label: 'Service active',     color: 'success' },
  case_email:         { label: 'Case email',         color: 'info' },
  ticket_email:       { label: 'Ticket email',       color: 'info' },
  review_request:     { label: 'Review request',     color: 'warning' },
  sms:                { label: 'SMS',                color: 'secondary' },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

interface Props {
  accountId: string;
  mobileNumber?: string | null;
  customerName?: string | null;
  contacts?: AccountContact[];
}

const SMS_MAX_LENGTH = 1600;

const CONTACT_TYPE_LABELS: Record<string, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  billing: 'Billing',
  admin: 'Admin',
};

const AccountCommunicationsTab: React.FC<Props> = ({ accountId, mobileNumber, customerName, contacts }) => {
  const [entries, setEntries] = useState<CommEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CommEntry | null>(null);

  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewSending, setReviewSending] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsRecipientId, setSmsRecipientId] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsSuccess, setSmsSuccess] = useState<string | null>(null);
  const [smsError, setSmsError] = useState<string | null>(null);

  const smsRecipients = useMemo(() => {
    const list: { id: string; label: string; mobile: string }[] = [];
    if (mobileNumber) {
      list.push({ id: 'primary', label: `${customerName || 'Primary contact'} (Primary)`, mobile: mobileNumber });
    }
    (contacts || []).forEach((c) => {
      if (c.mobile_number) {
        const typeLabel = CONTACT_TYPE_LABELS[c.contact_type] || c.contact_type;
        list.push({ id: c.id, label: `${c.full_name || 'Contact'} (${typeLabel})`, mobile: c.mobile_number });
      }
    });
    return list;
  }, [mobileNumber, customerName, contacts]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.getAccountCommunications(accountId, page + 1, perPage);
      setEntries(data.entries);
      setTotal(data.total);
    } catch {
      setError('Failed to load communications.');
    } finally {
      setLoading(false);
    }
  }, [accountId, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const handleSendSms = async () => {
    setSmsSending(true);
    setSmsError(null);
    try {
      const contactId = smsRecipientId === 'primary' ? null : smsRecipientId;
      const result = await ApiService.sendAccountSms(accountId, smsMessage.trim(), contactId);
      setSmsSuccess(`SMS sent to ${result.sent_to}`);
      setSmsDialogOpen(false);
      setSmsMessage('');
      load();
    } catch (err: any) {
      setSmsError(err?.response?.data?.detail || 'Failed to send SMS.');
    } finally {
      setSmsSending(false);
    }
  };

  const handleSendReviewRequest = async () => {
    setReviewSending(true);
    setReviewError(null);
    try {
      const result = await ApiService.adminSendGoogleReviewRequest(accountId);
      setReviewSuccess(`Review request sent to ${result.sent_to}`);
      setReviewDialogOpen(false);
      load();
    } catch (err: any) {
      setReviewError(err?.response?.data?.detail || 'Failed to send review request.');
    } finally {
      setReviewSending(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {reviewSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setReviewSuccess(null)}>
          {reviewSuccess}
        </Alert>
      )}
      {smsSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSmsSuccess(null)}>
          {smsSuccess}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SmsIcon />}
          disabled={smsRecipients.length === 0}
          onClick={() => {
            setSmsError(null);
            setSmsMessage('');
            setSmsRecipientId(smsRecipients[0]?.id ?? '');
            setSmsDialogOpen(true);
          }}
        >
          Send SMS
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<StarIcon />}
          onClick={() => { setReviewError(null); setReviewDialogOpen(true); }}
        >
          Send review request
        </Button>
      </Box>

      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No emails have been sent to this account yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => {
                const typeInfo = EMAIL_TYPE_LABELS[e.email_type] ?? { label: e.email_type, color: 'default' as const };
                return (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Chip
                        label={typeInfo.label}
                        color={typeInfo.color}
                        size="small"
                        sx={typeInfo.color === 'secondary' ? { color: '#fff' } : undefined}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.to_email}</TableCell>
                    <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.subject}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8125rem' }}>
                      {e.reference_label ?? '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8125rem' }}>
                      {formatDate(e.sent_at)}
                    </TableCell>
                    <TableCell padding="none">
                      <Tooltip title={e.body_html ? 'View email' : 'No preview available'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!e.body_html}
                            onClick={() => setSelected(e)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            rowsPerPageOptions={[perPage]}
          />
        </TableContainer>
      )}

      {/* Send SMS dialog */}
      <Dialog open={smsDialogOpen} onClose={() => (!smsSending ? setSmsDialogOpen(false) : undefined)} maxWidth="xs" fullWidth>
        <DialogTitle>Send SMS</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            To
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select
              value={smsRecipientId}
              onChange={(e) => setSmsRecipientId(e.target.value)}
              disabled={smsSending}
            >
              {smsRecipients.map((r) => (
                <MenuItem key={r.id} value={r.id}>{r.label} — {r.mobile}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            placeholder="Type your message…"
            value={smsMessage}
            onChange={(e) => setSmsMessage(e.target.value.slice(0, SMS_MAX_LENGTH))}
            helperText={`${smsMessage.length} / ${SMS_MAX_LENGTH}`}
            disabled={smsSending}
          />
          {smsError && <Alert severity="error" sx={{ mt: 2 }}>{smsError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSmsDialogOpen(false)} disabled={smsSending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendSms}
            disabled={smsSending || !smsMessage.trim() || !smsRecipientId}
            startIcon={smsSending ? <CircularProgress size={14} /> : <SmsIcon />}
          >
            {smsSending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send review request confirmation dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send Google Review Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: reviewError ? 2 : 0 }}>
            Send a Google review request email to this account using the template configured in Settings → Communications.
          </Typography>
          {reviewError && <Alert severity="error" sx={{ mt: 2 }}>{reviewError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={reviewSending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendReviewRequest}
            disabled={reviewSending}
            startIcon={reviewSending ? <CircularProgress size={14} /> : <StarIcon />}
          >
            {reviewSending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email preview modal */}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        {selected && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>{selected.subject}</Typography>
              <Typography variant="caption" color="text.secondary">
                To: {selected.to_email} &middot; {formatDate(selected.sent_at)}
                {selected.reference_label && ` · ${selected.reference_label}`}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setSelected(null)}
                sx={{ position: 'absolute', top: 12, right: 12 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: selected.email_type === 'sms' ? 2 : 0, overflow: selected.email_type === 'sms' ? 'auto' : 'hidden' }}>
              {selected.email_type === 'sms' ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selected.body_html}
                </Typography>
              ) : (
                <iframe
                  srcDoc={selected.body_html ?? ''}
                  title="Email preview"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  sandbox="allow-same-origin"
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AccountCommunicationsTab;
